import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './Paginator.css';

export interface PaginatorProps {
  /** Current page number (0-indexed) */
  currentPage: number;
  /** Number of items per page */
  pageSize: number;
  /** Total number of items */
  totalItems: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Optional class name */
  className?: string;
}

/**
 * Classic numbered paginator with page buttons and prev/next navigation.
 * Uses 0-indexed pages internally but displays 1-indexed to users.
 * Shows ellipsis (...) for large page counts.
 */
export const Paginator: React.FC<PaginatorProps> = ({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  className = '',
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  const canPrev = currentPage > 0;
  const canNext = currentPage < totalPages - 1;

  // Don't render if there's nothing to paginate
  if (totalItems === 0 || totalPages <= 1) {
    return null;
  }

  const handlePrev = () => {
    if (canPrev) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (canNext) {
      onPageChange(currentPage + 1);
    }
  };

  // Generate page numbers to display
  // Shows: [1] ... [current-1] [current] [current+1] ... [last]
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      // Show all pages
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(0);

      // Calculate range around current page
      const leftBound = Math.max(1, currentPage - 1);
      const rightBound = Math.min(totalPages - 2, currentPage + 1);

      // Add ellipsis after first page if needed
      if (leftBound > 1) {
        pages.push('ellipsis');
      }

      // Add pages around current
      for (let i = leftBound; i <= rightBound; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (rightBound < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`paginator ${className}`}>
      <button
        type="button"
        onClick={handlePrev}
        disabled={!canPrev}
        className="paginator-nav-button"
        aria-label="Previous page"
      >
        <ChevronLeft className="paginator-icon" />
      </button>

      <div className="paginator-pages">
        {pageNumbers.map((page, index) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="paginator-ellipsis">
              ...
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => onPageChange(page)}
              className={`paginator-page-button ${
                page === currentPage ? 'paginator-page-button--active' : ''
              }`}
              aria-label={`Page ${page + 1}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page + 1}
            </button>
          )
        )}
      </div>

      <button
        type="button"
        onClick={handleNext}
        disabled={!canNext}
        className="paginator-nav-button"
        aria-label="Next page"
      >
        <ChevronRight className="paginator-icon" />
      </button>

      <span className="paginator-total">
        {totalItems.toLocaleString()} total
      </span>
    </div>
  );
};

export default Paginator;
